import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  SearchCheck, Loader2, BadgeCheck, XCircle, CalendarDays, Clock3, MapPin, Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { SITE } from "../mock";
import { PayOnlineCard } from "../components/PayOnline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyBooking() {
  const [confNumber, setConfNumber] = useState("");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const conf = searchParams.get("conf");
    const em = searchParams.get("email");
    if (conf && em) {
      setConfNumber(conf);
      setEmail(em);
      doLookup(conf, em);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLookup = async (conf, em) => {
    setLoading(true);
    setBooking(null);
    try {
      const res = await axios.post(`${API}/bookings/lookup`, {
        confirmation_number: conf.trim(),
        email: em.trim(),
      });
      setBooking(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not find your booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const lookup = async () => {
    if (!confNumber.trim()) { toast.error("Please enter your confirmation number."); return; }
    if (!validEmail(email)) { toast.error("Please enter the email address used for the booking."); return; }
    await doLookup(confNumber, email);
  };

  const cancelBooking = async () => {
    setCancelling(true);
    try {
      const res = await axios.post(`${API}/bookings/cancel`, {
        confirmation_number: confNumber.trim(),
        email: email.trim(),
      });
      setBooking(res.data);
      toast.success("Your booking has been cancelled.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not cancel your booking. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  const isCancelled = booking?.status === "cancelled";
  const testDatePassed = booking && new Date(booking.date + "T23:59:59") < new Date();

  return (
    <div className="bg-[#F6FAFB] min-h-screen" data-testid="mybooking-page">
      <div className="bg-[#0A2540] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Manage Your Registration</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">Find My Booking</h1>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
            Enter your confirmation number and the email address used during registration to view or cancel your test booking.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-8">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="font-bold text-[#0A2540]">Confirmation Number *</Label>
                <Input
                  data-testid="lookup-confirmation-input"
                  value={confNumber}
                  onChange={(e) => setConfNumber(e.target.value.toUpperCase())}
                  placeholder="e.g. MBS-A1B2C3"
                  className="mt-2 rounded-lg uppercase"
                  onKeyDown={(e) => e.key === "Enter" && lookup()}
                />
              </div>
              <div>
                <Label className="font-bold text-[#0A2540]">Email Address *</Label>
                <Input
                  data-testid="lookup-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@company.com"
                  className="mt-2 rounded-lg"
                  onKeyDown={(e) => e.key === "Enter" && lookup()}
                />
              </div>
            </div>
            <Button
              data-testid="lookup-submit-btn"
              onClick={lookup}
              disabled={loading}
              className="mt-6 bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg px-8 h-11 shadow-md"
            >
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Searching...</> : <><SearchCheck size={16} className="mr-2" /> Find My Booking</>}
            </Button>
            <p className="text-xs text-slate-400 mt-4">
              Your confirmation number was shown on the confirmation page and sent in your confirmation email. Can't find it? Call us at {SITE.phone}.
            </p>
          </CardContent>
        </Card>

        {booking && (
          <Card className="rounded-2xl border-slate-100 shadow-lg overflow-hidden mt-8" data-testid="booking-result-card">
            <div className={`p-6 text-center ${isCancelled ? "bg-red-500" : "bg-[#0E9AAA]"}`}>
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                {isCancelled ? <XCircle className="text-white" size={26} /> : <BadgeCheck className="text-white" size={26} />}
              </div>
              <h2 className="text-xl font-extrabold text-white">
                {isCancelled ? "This Booking Has Been Cancelled" : "Booking Confirmed"}
              </h2>
              <p className="text-white/90 mt-1 text-sm">
                Confirmation Number: <span className="font-extrabold">{booking.confirmation_number}</span>
              </p>
            </div>
            <CardContent className="p-8">
              <div className="grid sm:grid-cols-2 gap-5 text-sm">
                <div className="sm:col-span-2">
                  <div className="text-slate-400 font-semibold uppercase text-xs">Test</div>
                  <div className="font-bold text-[#0A2540] mt-1">{booking.test_name}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-semibold uppercase text-xs flex items-center gap-1"><CalendarDays size={12} /> Date</div>
                  <div className="font-bold text-[#0A2540] mt-1">{booking.date_display || booking.date}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-semibold uppercase text-xs flex items-center gap-1"><Clock3 size={12} /> Time</div>
                  <div className="font-bold text-[#0A2540] mt-1">{booking.time_slot}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-semibold uppercase text-xs flex items-center gap-1"><Users size={12} /> Testee(s)</div>
                  <div className="font-bold text-[#0A2540] mt-1">{(booking.testees || []).map((t) => t.full_name).join(", ")}</div>
                </div>
                <div>
                  <div className="text-slate-400 font-semibold uppercase text-xs">Total Cost</div>
                  <div className="font-bold text-[#0A2540] mt-1">CI ${Number(booking.total_cost).toFixed(2)}</div>
                </div>
                <div className="sm:col-span-2">
                  <div className="text-slate-400 font-semibold uppercase text-xs flex items-center gap-1"><MapPin size={12} /> Location</div>
                  <div className="font-bold text-[#0A2540] mt-1">{SITE.address} — {SITE.addressNote}</div>
                </div>
                <div className="sm:col-span-2 flex items-center gap-2">
                  <span className="text-slate-400 font-semibold uppercase text-xs">Status:</span>
                  <Badge className={isCancelled ? "bg-red-50 text-red-500 hover:bg-red-50 border border-red-100" : "bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC]"}>
                    {booking.status}
                  </Badge>
                  {booking.payment_status === "paid_online" && (
                    <Badge className="bg-emerald-50 text-emerald-600 hover:bg-emerald-50 border border-emerald-200" data-testid="mybooking-paid-badge">
                      Paid Online
                    </Badge>
                  )}
                </div>
              </div>

              {!isCancelled && !testDatePassed && (
                <div className="mt-6">
                  <PayOnlineCard
                    confirmationNumber={booking.confirmation_number}
                    email={email.trim()}
                    totalCost={booking.total_cost}
                    paymentStatus={booking.payment_status}
                  />
                </div>
              )}

              {!isCancelled && !testDatePassed && (
                <div className="mt-7 pt-6 border-t border-slate-100">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-testid="cancel-own-booking-btn"
                        variant="outline"
                        disabled={cancelling}
                        className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 font-bold rounded-lg"
                      >
                        {cancelling ? <Loader2 size={16} className="mr-2 animate-spin" /> : <XCircle size={16} className="mr-2" />}
                        Cancel This Booking
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel your booking?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will cancel booking {booking.confirmation_number} for {booking.date_display || booking.date} at {booking.time_slot}. This action cannot be undone — you would need to register again to book a new test.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-lg font-bold">Keep My Booking</AlertDialogCancel>
                        <AlertDialogAction onClick={cancelBooking} className="bg-red-500 hover:bg-red-600 rounded-lg font-bold">
                          Yes, Cancel It
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <p className="text-xs text-slate-400 mt-3">
                    Need to reschedule instead? Cancel this booking and register again, or call us at {SITE.phone}.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
